/**
 * Floating "+points" labels at merge sites.
 *
 * On every merge the game spawns a popup at the merge world-space
 * position. The label is a DOM <span> projected onto screen space via
 * the camera, then animated upward with a fade. Pure visual — no
 * gameplay impact.
 *
 * Uses DOM rather than Three.js sprites so text rendering stays crisp
 * at any DPI without additional canvas atlases.
 */

import type { Camera, Vector3 as Vec3T } from "three";
import { Vector3 } from "three";

import { formatNumber } from "../i18n/index.ts";

const LIFETIME_MS = 900;
const RISE_PX = 64;

interface ActivePopup {
  element: HTMLDivElement;
  spawnedAt: number;
  worldPos: Vec3T;
  startScreenY: number;
}

export interface SpawnPopupOptions {
  position: { x: number; y: number; z: number };
  /** Numeric points value — formatted as "+N". Ignored if `label` is set. */
  points?: number;
  /** Override the rendered text entirely (e.g. "+3 AMMO"). */
  label?: string;
  color: number;
  /** Optional combo multiplier suffix (× N). */
  multiplier?: number;
}

export interface ScorePopupSystem {
  spawn(opts: SpawnPopupOptions): void;
  tick(nowMs: number, viewport: { width: number; height: number }): void;
  detach(): void;
}

export interface ScorePopupOptions {
  host: HTMLElement;
  camera: Camera;
}

function hexToCss(hex: number): string {
  return `#${hex.toString(16).padStart(6, "0")}`;
}

export function createScorePopupSystem(
  opts: ScorePopupOptions,
): ScorePopupSystem {
  const layer = document.createElement("div");
  layer.dataset["testid"] = "score-popup-layer";
  layer.className = "pointer-events-none absolute inset-0 overflow-hidden";
  opts.host.appendChild(layer);

  const active: ActivePopup[] = [];
  const projection = new Vector3();

  const spawn = ({
    position,
    points,
    label,
    color,
    multiplier,
  }: SpawnPopupOptions): void => {
    const element = document.createElement("div");
    element.dataset["testid"] = "score-popup";
    element.className =
      "absolute font-display text-xl font-bold drop-shadow-[0_2px_4px_rgba(0,0,0,0.85)]";
    const multSuffix =
      multiplier && multiplier > 1 ? ` ×${multiplier}` : "";
    element.textContent =
      label !== undefined
        ? `${label}${multSuffix}`
        : `+${formatNumber(points ?? 0)}${multSuffix}`;
    element.style.color = hexToCss(color);
    element.style.transform = "translate(-50%, -50%)";
    element.style.left = "0px";
    element.style.top = "0px";
    layer.appendChild(element);
    const worldPos = new Vector3(position.x, position.y, position.z);
    active.push({
      element,
      spawnedAt: typeof performance !== "undefined" ? performance.now() : 0,
      worldPos,
      startScreenY: 0,
    });
  };

  const removeAt = (idx: number): void => {
    const popup = active[idx];
    if (!popup) return;
    popup.element.remove();
    active.splice(idx, 1);
  };

  const tick = (
    nowMs: number,
    viewport: { width: number; height: number },
  ): void => {
    for (let i = active.length - 1; i >= 0; i -= 1) {
      const p = active[i]!;
      const elapsed = nowMs - p.spawnedAt;
      const k = Math.min(1, elapsed / LIFETIME_MS);
      // Project world → NDC → screen pixel.
      projection.copy(p.worldPos);
      projection.project(opts.camera);
      const screenX = (projection.x * 0.5 + 0.5) * viewport.width;
      const screenY = (-projection.y * 0.5 + 0.5) * viewport.height;
      const offsetY = -RISE_PX * (1 - Math.pow(1 - k, 2));
      p.element.style.left = `${screenX}px`;
      p.element.style.top = `${screenY + offsetY}px`;
      p.element.style.opacity = `${1 - k}`;
      if (elapsed >= LIFETIME_MS) {
        removeAt(i);
      }
    }
  };

  const detach = (): void => {
    while (active.length > 0) removeAt(active.length - 1);
    layer.remove();
  };

  return { spawn, tick, detach };
}

export const __testing = { LIFETIME_MS, RISE_PX };
